/**
 * A cycle's set of review questions, and what may be done to it.
 *
 * The set belongs to a cycle rather than to the product, and that scoping is
 * the whole design: one cycle can ask six questions where the last asked four,
 * and a report comparing the two is only honest if each set is the words people
 * actually answered. So a set has three states and only the first is editable —
 * draft, open, closed — and everything here follows from which one it is in.
 *
 * Named `questionSet` rather than `reviewQuestions` on purpose: the screen next
 * door is `ReviewQuestionsScreen`, and on a case-insensitive filesystem a
 * `reviewQuestions.js` beside a `ReviewQuestions.js` is one file, not two.
 */

// Spelled out in full, as the design writes it ("on 24 March"), and from a
// table rather than toLocaleDateString — which renders September as "Sept" and
// abbreviates nothing else, so one month in twelve looks like a typo.
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** "24 March" — day then month. */
export const dayMonth = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

export const ASKED_OF = {
  self: { label: "Employee", tone: "info" },
  manager: { label: "Manager", tone: "muted" },
  both: { label: "Both", tone: "success" },
};

export const ASKED_OF_OPTIONS = ["self", "manager", "both"];

/**
 * A cycle's state, in the words this screen uses.
 *
 * The cycle's own `status` is draft/active/closed; a question set reads as
 * draft/open/closed, because "active" describes a cycle that is running and
 * "open" describes a set that can be answered. Same fact, and the label is
 * chosen for what the reader is looking at.
 */
export const setState = (cycle) => {
  switch (cycle?.status) {
    case "draft":
      return { key: "draft", label: "Draft · not yet open", tone: "muted", editable: true };
    case "active":
      return { key: "open", label: "Open", tone: "info", editable: false };
    case "closed":
      return { key: "closed", label: "Closed · read-only", tone: "success", editable: false };
    default:
      return { key: "unknown", label: "Unknown", tone: "muted", editable: false };
  }
};

/**
 * Why a set cannot be edited, in terms of what is already attached to it.
 *
 * A locked table with no explanation reads as broken. The count matters more
 * than the rule: "124 self-reviews already in" is why the words cannot move,
 * where "the cycle is active" is only a restatement of the greyed-out controls.
 */
export const lockNote = (cycle, counts = {}) => {
  const state = setState(cycle);
  if (state.editable) return "This set is a draft and nobody can see it yet.";
  if (state.key === "open") {
    const n = counts.self || 0;
    return n
      ? `${cycle.name} is open with ${n} self-review${n === 1 ? "" : "s"} already in, so its questions are locked.`
      : `${cycle.name} is open, so its questions are locked.`;
  }
  if (state.key === "closed") {
    const total = (counts.self || 0) + (counts.manager || 0);
    return total
      ? `A closed cycle cannot be edited at all, not even its wording. ${total} review${total === 1 ? " is" : "s are"} attached to these exact words.`
      : "A closed cycle cannot be edited at all, not even its wording.";
  }
  return null;
};

/** "6 questions · 5 required · started from H1 2026 by People Ops on 24 March" */
export const setSummary = (cycle, sourceName) => {
  const qs = cycle?.questions || [];
  const required = qs.filter((q) => q.required !== false).length;
  const parts = [
    `${qs.length} question${qs.length === 1 ? "" : "s"}`,
    `${required} required`,
  ];
  const meta = cycle?.questionsMeta;
  const who = meta?.by?.fname ? `${meta.by.fname} ${meta.by.lname || ""}`.trim() : null;
  const when = dayMonth(meta?.at);
  if (sourceName || who || when) {
    const tail = [
      sourceName ? `started from ${sourceName}` : "set",
      who ? `by ${who}` : null,
      when ? `on ${when}` : null,
    ].filter(Boolean).join(" ");
    parts.push(tail);
  }
  return parts.join(" · ");
};

/** "138 self-reviews and 138 manager reviews submitted against this set" */
export const answeredSummary = (counts = {}) => {
  const s = counts.self || 0;
  const m = counts.manager || 0;
  if (!s && !m) return "Nothing submitted against this set yet";
  return `${s} self-review${s === 1 ? "" : "s"} and ${m} manager review${m === 1 ? "" : "s"} submitted against this set`;
};

/**
 * How many reviews have actually been written against a set.
 *
 * Counted from `submittedAt` rather than from the review's status: a review can
 * move to `manager_completed` while its self half was never filled in, and
 * saying "138 self-reviews" about a set where 40 people never wrote one would
 * misrepresent what the questions collected.
 */
export const answerCounts = (reviews = []) => ({
  self: (reviews || []).filter((r) => r?.self?.submittedAt).length,
  manager: (reviews || []).filter((r) => r?.manager?.submittedAt).length,
});

/** Move a row within the ordered list. Out-of-range moves are no-ops, not errors. */
export const reorder = (questions = [], from, to) => {
  const list = [...(questions || [])];
  if (from < 0 || from >= list.length || to < 0 || to >= list.length || from === to) return list;
  const [row] = list.splice(from, 1);
  list.splice(to, 0, row);
  return list;
};

/**
 * The draft a closed or open set can be copied into.
 *
 * Only a draft can receive one, and never the set being read: copying a cycle
 * into itself is either a no-op or a way to lose the very words the screen
 * exists to preserve.
 */
export const copyTarget = (cycles = [], sourceId) =>
  (cycles || []).find(
    (c) => c?.status === "draft" && String(c._id) !== String(sourceId)
  ) || null;

/**
 * Questions ready to be written to another cycle.
 *
 * Ids are dropped deliberately, which is the opposite of the rule inside a
 * single set. Within a cycle an id must survive an edit, because answers are
 * stored against it. Across cycles it must not: the new set is a new
 * instrument, its answers are its own, and reusing an id would make two
 * cycles' answers indistinguishable.
 */
export const questionsForCopy = (questions = []) =>
  (questions || []).map((q) => ({
    prompt: q.prompt,
    hint: q.hint,
    required: q.required !== false,
    askedOf: q.askedOf || "both",
  }));

/** A question with nothing written in it is not a question. */
export const cleanQuestions = (questions = []) =>
  (questions || [])
    .filter((q) => (q.prompt || "").trim())
    .map((q) => ({
      ...(q._id ? { _id: q._id } : {}),
      prompt: q.prompt.trim(),
      ...(q.hint ? { hint: q.hint.trim() } : {}),
      required: q.required !== false,
      askedOf: q.askedOf || "both",
    }));

/** Whether the draft differs from what is stored, so Save can stay inert. */
export const isDirty = (stored = [], draft = []) => {
  const norm = (list) =>
    JSON.stringify(
      (list || []).map((q) => [
        (q.prompt || "").trim(),
        q.askedOf || "both",
        q.required !== false,
      ])
    );
  return norm(stored) !== norm(draft);
};
